import { EnvType } from "../services";
import IApiClient from "../api-client";
import { GenericResponseStd } from "../Types/generic-response-std";
import { ActivityKind } from "../Types/activity-kind";
import { GetActivitiesResponse } from "../Types/get-activities-response";
import { GetActivityEnumsResponse } from "../Types/get-activity-enums-response";
import { GetUserActivitiesCountResponse } from "../Types/get-user-activities-count-response";
import { CreateActivityResponse } from "../Types/create-activity-response";
import { CreateActivityRequest } from "../Types/create-activity-request";
import { GetActivitiesByTypesRequest } from "../Types/get-activities-by-types-request";

interface ILogApiClient {
  ListAllActivitiesByPeriod(beginAsString: string, endAsString: string): Promise<GetActivitiesResponse>;
  ListAllActivitiesByPage(pageNb: number, pageSize: number): Promise<GetActivitiesResponse>;
  ListAllUserActivities(userId: number): Promise<GetActivitiesResponse>;
  ListAllUserActivitiesByPeriod(userId: number, dateIn: string, dateOut: string): Promise<GetActivitiesResponse>;
  ListSessionUserActivities(pageNb: number, pageSize: number): Promise<GetActivitiesResponse>;
  ReadActivity(id: number): Promise<GenericResponseStd>;
  SolveActivity(id: number): Promise<GenericResponseStd>;
  ReadAllActivities(id: number): Promise<GenericResponseStd>;
  CountUserActivities(userId: number): Promise<GetUserActivitiesCountResponse>;
  ListResourceActivities(
    resourceKind: ActivityKind,
    resourceId: number,
    pageNb: number,
    pageSize: number
  ): Promise<GetActivitiesResponse>;
  GetUserActionsEnums(): Promise<GetActivityEnumsResponse>;
}

export default class LogApiClient implements ILogApiClient {
  readonly servicePort = 9511;
  readonly serviceBaseUrl = "log-service/api";

  logApiClient: IApiClient;

  constructor(logApiClient: IApiClient, envType: EnvType) {
    this.logApiClient = logApiClient;
    if (envType === "local") this.logApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  async ListAllActivitiesByPeriod(beginAsString: string, endAsString: string): Promise<GetActivitiesResponse> {
    return await this.logApiClient.get(`${this.serviceBaseUrl}/activities/period/${beginAsString}/${endAsString}`);
  }
  async ListAllActivitiesByPage(pageNb: number, pageSize: number): Promise<GetActivitiesResponse> {
    return await this.logApiClient.get(`${this.serviceBaseUrl}/activities/page/${pageNb}/${pageSize}`);
  }
  async ListAllUserActivities(userId: number): Promise<GetActivitiesResponse> {
    return await this.logApiClient.get(`${this.serviceBaseUrl}/activities/user/${userId}`);
  }
  async ListAllUserActivitiesByPeriod(userId: number, dateIn: string, dateOut: string): Promise<GetActivitiesResponse> {
    return await this.logApiClient.get(`${this.serviceBaseUrl}/activities/user/${userId}/period/${dateIn}/${dateOut}`);
  }
  async ListSessionUserActivities(pageNb: number, pageSize: number): Promise<GetActivitiesResponse> {
    return await this.logApiClient.get(`${this.serviceBaseUrl}/activities/user/mine/page/${pageNb}/${pageSize}`);
  }
  async ListSessionUserActivitiesByTypes(request: GetActivitiesByTypesRequest): Promise<GetActivitiesResponse> {
    return await this.logApiClient.post(`${this.serviceBaseUrl}/activities/user/mine/activities-by-types`, request);
  }
  async ReadActivity(id: number): Promise<GenericResponseStd> {
    return await this.logApiClient.put(`${this.serviceBaseUrl}/activities/user/mine/read/${id}`);
  }
  async SolveActivity(id: number): Promise<GenericResponseStd> {
    return await this.logApiClient.put(`${this.serviceBaseUrl}/activities/user/mine/solve/${id}`);
  }
  async ReadAllActivities(id: number): Promise<GenericResponseStd> {
    return await this.logApiClient.put(`${this.serviceBaseUrl}/activities/user/mine/read/all`, id);
  }
  async CountUserActivities(userId: number): Promise<GetUserActivitiesCountResponse> {
    return await this.logApiClient.get(`${this.serviceBaseUrl}/activities/user/count/${userId}`);
  }
  async ListResourceActivities(
    resourceKind: ActivityKind,
    resourceId: number,
    pageNb: number,
    pageSize: number
  ): Promise<GetActivitiesResponse> {
    return await this.logApiClient.get(
      `${this.serviceBaseUrl}/activities/resource/${resourceKind}/${resourceId}/page/${pageNb}/${pageSize}`
    );
  }
  async GetUserActionsEnums(): Promise<GetActivityEnumsResponse> {
    return await this.logApiClient.get(`${this.serviceBaseUrl}/activities/enums`);
  }
  async InsertActivityComment(request: CreateActivityRequest): Promise<CreateActivityResponse> {
    return await this.logApiClient.post(`${this.serviceBaseUrl}/activities/comment`, request);
  }

  //------Utils------
  GetApiUrl(): string {
    return this.logApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
